import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { meldungsIdAnzeige } from "../design/Kennung";
import { PersonMitBezeichnung } from "../design/Person";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import type { Personenangabe, Rolle } from "@dj/domain/gemeinsam";
import { useDokument, useSammlung, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";
import { navigieren, positionText } from "./geraeteposition";
import { ansprache, useEigenesRevier, useRevierSammlung, useRollen, type Rahmen } from "./lage";

/**
 * Offene Suchen (oberflaeche.md §6, Domänenmodell §13; F-150 … F-157): je Schuss ein `article` „<Suchart> Stand <Nr>“
 * mit der Meldungs-Id, Anschussposition und Quelle, Fluchtrichtung und zugewiesenem Hundeführer; „Zuweisen“ öffnet den
 * Hundeführer-Vorschlag, „Navigation“ übergibt den Anschuss an die Karten-App. Eine Kontrollsuche schließt „Abschließen“
 * mit Begründung als nicht erforderlich ab (Dialog „Kontrollsuche“), eine Suche nach „nicht gefunden“ ohne Begründung.
 * Ob die Person zuweisen darf, entscheidet der Server (rolle-fehlt).
 *
 * Wildfolge (F-158, schuss/11, V-24): In einer gemeinsamen Drückjagd kennzeichnen Jagdherr und Jagdleiter einen Anschuss
 * ihres Reviers mit `button` „Wildfolge“ (`alertdialog` „Wildfolge“, kein Rückgängig: die Leitung der Nachbarreviere wird
 * sofort benachrichtigt); danach steht `Wildfolge` im Eintrag. Für Wildfolge gekennzeichnete Anschüsse der anderen
 * beteiligten Reviere stehen für die Leitung in der `region` „Wildfolge“ mit Revier, Ansprache, Fluchtrichtung,
 * Anschussposition und `button` „Navigation“ (lesemodelle.md §3).
 */
export function OffeneSuchen({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const revier = useEigenesRevier(id);
  const suchen = useRevierSammlung(id, revier, "offene_suchen");
  const wildfolge = useWildfolge(id, revier);
  const [kennzeichnen, setKennzeichnen] = useState<string | null>(null);
  const [kontrollsuche, setKontrollsuche] = useState<string | null>(null);
  const [begruendung, setBegruendung] = useState("");
  const [abgewiesen, setAbgewiesen] = useState<string | null>(null);

  const senden = async (name: string, daten: Record<string, unknown>) => {
    const a = await befehl(name, daten);
    setAbgewiesen(a.netzfehler ? null : abweisungsgrund(a));
  };

  const nichtErforderlich = async () => {
    const schuss = kontrollsuche;
    setKontrollsuche(null);
    if (schuss) await senden("KontrollsucheNichtErforderlichAbschliessen", { Schuss: schuss, ...(begruendung.trim() ? { "Begründung": begruendung.trim() } : {}) });
    setBegruendung("");
  };

  const wildfolgeKennzeichnen = async () => {
    const schuss = kennzeichnen;
    setKennzeichnen(null);
    if (schuss) await senden("WildfolgeKennzeichnen", { Schuss: schuss });
  };

  const sortiert = [...suchen].sort((a, b) => String(a["Schuss"]).localeCompare(String(b["Schuss"])));
  return rahmen({
    drueckjagdId: id, name: "Offene Suchen",
    children: (
      <section className="liste-bereich" role="region" aria-label="Offene Suchen">
        <Abweisung grund={abgewiesen} />
        {sortiert.map((s) => <Suche key={s._id} suche={s} wildfolge={wildfolge.kennzeichenbar ? wildfolge.eigene.has(String(s["Schuss"])) : null}
          onWildfolge={() => setKennzeichnen(String(s["Schuss"]))}
          onZuweisen={() => navigate(`/drueckjagd/${id}/offene-suchen/${encodeURIComponent(String(s["Schuss"]))}`)}
          onAbschliessen={() => (s["Schussstatus"] === "nicht gefunden"
            ? void senden("SucheNichtGefundenAbschliessen", { Schuss: s["Schuss"] })
            : setKontrollsuche(String(s["Schuss"])))} />)}
        {sortiert.length === 0 ? <p className="leer platte"><Symbol name="nachsuche" /><span>Keine offenen Suchen</span></p> : null}
        {wildfolge.fremde.length ? <WildfolgeDerNachbarn anschuesse={wildfolge.fremde} /> : null}
        <Dialog name="Wildfolge" symbol="fluchtrichtung" offen={!!kennzeichnen} onSchliessen={() => setKennzeichnen(null)} bestaetigung>
          <p className="erkl">{kennzeichnen ? `${meldungsIdAnzeige(kennzeichnen)} · sofort für die Nachbarreviere · kein Rückgängig` : null}</p>
          <div className="knopfreihe">
            <Knopf onPress={() => void wildfolgeKennzeichnen()}>Bestätigen</Knopf>
            <Knopf art="zweit" onPress={() => setKennzeichnen(null)}>Abbrechen</Knopf>
          </div>
        </Dialog>
        <Dialog name="Kontrollsuche" symbol="kontrollsuche" offen={!!kontrollsuche} onSchliessen={() => setKontrollsuche(null)}>
          <Textfeld label="Begründung" wert={begruendung} onChange={setBegruendung} />
          <div className="knopfreihe">
            <Knopf art="zweit" onPress={() => setKontrollsuche(null)}>Abbrechen</Knopf>
            <Knopf onPress={() => void nichtErforderlich()}>Speichern</Knopf>
          </div>
        </Dialog>
      </section>
    ),
  });
}

/**
 * Wildfolge in dieser Drückjagd für die Leitung: ob sie gemeinsam ist (eigenes Revier beteiligt statt Veranstalter, oder
 * der Veranstalter hat ein angenommenes Revier), welche Anschüsse des eigenen Reviers schon gekennzeichnet sind und welche
 * der anderen Reviere gezeigt werden.
 */
function useWildfolge(id: string, revier: string | null) {
  const { uid } = useSitzung();
  const rollen = useRollen(id);
  const leitung = rollen.includes("Jagdherr") || rollen.includes("Jagdleiter");
  const veranstalter = (useDokument(uid ? `personen/${uid}/meine_drueckjagden/${id}` : null).daten?.["_revier"] as string | undefined) ?? null;
  const beteiligungen = useSammlung(leitung && revier && revier === veranstalter ? `drueckjagden/${id}/beteiligungen` : null).daten;
  const gekennzeichnet = useSammlung(leitung ? `drueckjagden/${id}/wildfolge` : null).daten;
  const gemeinsam = !!revier && !!veranstalter && (revier !== veranstalter || beteiligungen.some((b) => b["Beteiligungsstatus"] === "angenommen"));
  const revierDes = (z: Zeile) => (z["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
  return {
    kennzeichenbar: leitung && gemeinsam,
    eigene: new Set(gekennzeichnet.filter((z) => revierDes(z) === revier).map((z) => String(z["Schuss"] ?? z._id))),
    fremde: gekennzeichnet.filter((z) => revierDes(z) !== revier).sort((a, b) => String(a["Schuss"]).localeCompare(String(b["Schuss"]))),
  };
}

/** Für Wildfolge gekennzeichnete Anschüsse der anderen beteiligten Reviere (lesemodelle.md §3 Wildfolge). */
export function WildfolgeDerNachbarn({ anschuesse }: { anschuesse: Zeile[] }) {
  return (
    <section className="liste-bereich" role="region" aria-label="Wildfolge">
      <h2 className="kopf gruppenkopf platte"><Symbol name="fluchtrichtung" />Wildfolge</h2>
      {anschuesse.map((w) => {
        const titelId = `wildfolge-${w._id}`;
        const position = w["Anschussposition"] as { Breite: number; Länge: number } | null;
        return (
          <article key={w._id} className="eintrag eintrag--knoepfe platte" aria-labelledby={titelId}>
            <Symbol name="anschuss" />
            <h3 className="eintrag__t" id={titelId}>{`Anschuss ${meldungsIdAnzeige(w["Schuss"])}`}</h3>
            <span className="eintrag__meta">{[w["Revier"], ansprache(w)].filter(Boolean).join(" · ")}</span>
            <dl className="angaben">
              <div><dt>Fluchtrichtung</dt><dd>{String(w["Fluchtrichtung"] ?? "—")}</dd></div>
              <div><dt>Anschussposition</dt><dd className="kennung">{positionText(position)}</dd></div>
              <div><dt>Quelle Anschussposition</dt><dd>{String(w["Quelle Anschussposition"] ?? "—")}</dd></div>
            </dl>
            <div className="raster raster--knoepfe">
              <Kachel symbol="navigation" begriff="Navigation" art="kompakt" onPress={() => navigieren(position)} />
            </div>
          </article>
        );
      })}
    </section>
  );
}

/** `wildfolge`: `null` = nicht kennzeichenbar (keine Leitung oder keine gemeinsame Drückjagd), sonst ob schon gekennzeichnet. */
function Suche({ suche, wildfolge, onWildfolge, onZuweisen, onAbschliessen }: {
  suche: Zeile; wildfolge: boolean | null; onWildfolge: () => void; onZuweisen: () => void; onAbschliessen: () => void;
}) {
  const titel = `${String(suche["Suchart"])} Stand ${String(suche["Stand"] ?? "—")}`;
  const titelId = `suche-${suche._id}`;
  const hf = suche["Zugewiesener Hundeführer"] as Personenangabe | null;
  const abschliessbar = suche["Schussstatus"] === "Kontrollsuche erforderlich" || suche["Schussstatus"] === "nicht gefunden";
  return (
    <article className="eintrag eintrag--knoepfe platte" aria-labelledby={titelId}>
        <Symbol name={suche["Suchart"] === "Kontrollsuche" ? "kontrollsuche" : "nachsuche"} />
        <h3 className="eintrag__t" id={titelId}>{titel}</h3>
        <span className="eintrag__meta kennung">{meldungsIdAnzeige(suche["Schuss"])}</span>
          <dl className="angaben">
            <div><dt>Ansprache</dt><dd>{String(suche["Ansprache"] ?? "—")}</dd></div>
            <div><dt>Schussstatus</dt><dd>{String(suche["Schussstatus"] ?? "—")}</dd></div>
            <div><dt>Fluchtrichtung</dt><dd>{String(suche["Fluchtrichtung"] ?? "—")}</dd></div>
            <div><dt>Anschussposition</dt><dd className="kennung">{positionText(suche["Anschussposition"] as { Breite: number; Länge: number } | null)}</dd></div>
            <div><dt>Quelle Anschussposition</dt><dd>{String(suche["Quelle Anschussposition"] ?? "—")}</dd></div>
            <div><dt>Schusszeichen</dt><dd>{String(suche["Schusszeichen"] ?? "—")}</dd></div>
            <div><dt>Pirschzeichen</dt><dd>{((suche["Pirschzeichen"] as string[] | undefined) ?? []).join(", ") || "—"}</dd></div>
            <div><dt>Zugewiesener Hundeführer</dt><dd>{hf ? hf.Name : "—"}</dd></div>
            {wildfolge ? <div><dt>Wildfolge</dt><dd>gekennzeichnet</dd></div> : null}
          </dl>
        <div className="raster raster--knoepfe">
          <Kachel symbol="zuweisen" begriff="Zuweisen" art="kompakt" onPress={onZuweisen} />
          <Kachel symbol="navigation" begriff="Navigation" art="kompakt" onPress={() => navigieren(suche["Anschussposition"] as { Breite: number; Länge: number } | null)} />
          {abschliessbar ? <Kachel symbol="haken" begriff="Abschließen" art="kompakt" onPress={onAbschliessen} /> : null}
          {wildfolge === false && suche["Suchart"] === "Nachsuche" ? <Kachel symbol="fluchtrichtung" begriff="Wildfolge" art="kompakt" onPress={onWildfolge} /> : null}
        </div>
    </article>
  );
}

/**
 * Hundeführer-Vorschlag (oberflaeche.md §6, F-151, K38): `region` „vorgeschlagene Hundeführer“ mit `heading`
 * „Hundeführende“; je Person ein `article` „<Name>“ mit Rolle, Kennzeichen und „Zuweisen“ — bestätigte
 * Nachsuchenführer zuerst. Bei laufender Suche ändert „Zuweisen“ die Zuweisung.
 */
export function HundefuehrerVorschlag({ rahmen }: { rahmen: Rahmen }) {
  const { id = "", schuss = "" } = useParams();
  const navigate = useNavigate();
  const revier = useEigenesRevier(id);
  const vorschlag = useRevierSammlung(id, revier, `nachsuche_vorschlag/${schuss}/hundefuehrer`);
  const suche = useRevierSammlung(id, revier, "offene_suchen").find((s) => s["Schuss"] === schuss);
  const [abgewiesen, setAbgewiesen] = useState<string | null>(null);
  const sortiert = [...vorschlag].sort((a, b) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0));

  const zuweisen = async (hf: string) => {
    const laeuft = !!suche?.["Zugewiesener Hundeführer"];
    const a = await befehl(laeuft ? "ZuweisungAendern" : "NachsucheZuweisen",
      laeuft ? { Schuss: schuss, "Hundeführer": hf } : { Schuss: schuss, Suchart: suche?.["Suchart"] ?? "Nachsuche", "Hundeführer": hf });
    if (a.ok || a.netzfehler) navigate(`/drueckjagd/${id}/offene-suchen`, { replace: true });
    else setAbgewiesen(abweisungsgrund(a));
  };

  return rahmen({
    drueckjagdId: id, name: "Hundeführer-Vorschlag",
    children: (
      <section className="liste-bereich" role="region" aria-label="vorgeschlagene Hundeführer">
        <h2 className="kopf gruppenkopf platte"><Symbol name="hundefuehrer" />Hundeführende</h2>
        <Abweisung grund={abgewiesen} />
        {sortiert.map((h) => {
          const person = h["Name"] as Personenangabe;
          const kennzeichen = h["Kennzeichen"] as string | null;
          return (
            <article key={h._id} className="eintrag eintrag--aktion platte" aria-label={person.Name}>
              <PersonMitBezeichnung person={person} rollen={String(h["Rolle"] ?? "").split(", ").filter(Boolean) as Rolle[]} />
              <span>{[h["Revier"], kennzeichen].filter(Boolean).join(" · ")}</span>
              <Kachel symbol="zuweisen" begriff="Zuweisen" art="kompakt" onPress={() => void zuweisen(String((h["_kennungen"] as Record<string, string> | undefined)?.["Name"] ?? h._id))} />
            </article>
          );
        })}
      </section>
    ),
  });
}
